import React, {useEffect, useState} from "react";
import {ReceivingTransmission} from "../ai/ai";
import {SpokenLine, useSpeech} from "../../common/spokenLine";
import {AnimatedPoints, HighScores, STROKE_FILTER, rankAccent} from "../../common/scores";

const PODIUM = {
    podium3: {spot: 3, label: 'THIRD PLACE'},
    podium2: {spot: 2, label: 'SECOND PLACE'},
    podium1: {spot: 1, label: 'YOUR CHAMPION'},
};

// The recap is written and spoken by the server (server/games/leaderboard/director.rb), a sentence at a time with
// the scores each is about; screens draw it.
export const Leaderboard = ({isControl, type = 'mid', receiveEvents, clockDrift}) => {
    if(!isControl) return <ReceivingTransmission />
    else return <LeaderboardControl type={type} receiveEvents={receiveEvents} clockDrift={clockDrift} />
}

// The server starts writing the recap when the show reaches this scene, so it's ready by <Leaderboard/>.
export const PrepareLeaderboard = () => null;

export const LeaderboardControl = ({type, receiveEvents, clockDrift}) => {
    const [live, setLive] = useState(null);
    const speech = useSpeech(receiveEvents);

    useEffect(() => {
        fetch('/leaderboard/live', {credentials: 'include'}).then(r => r.json()).then(d => setLive(d.leaderboard || null)).catch(() => {});
    }, []);
    useEffect(() => receiveEvents && receiveEvents((data) => {
        if (data && 'leaderboard' in data) setLive(data.leaderboard || null);
    }), [receiveEvents]);

    const sentences = (live && live.sentences) || [];
    const index = live ? live.index : 0;
    const showAllResults = !!(live && live.showAll);
    const current = sentences[index];
    const currentText = current && current.speech;
    const scores = current && current.scores;
    const podium = current && PODIUM[current.role];
    const line = live && speech && speech.id === `leaderboard:line:${live.scene}:${index}` ? speech : null;

    const indicator = (score) => {
        if(type === 'end') {
            return "";
        } else if(!score.trend || !score.rank_change) {
            return "";
        } else if(score.trend === "new") {
            return "🆕";
        } else if(score.rank_change > 0) {
            return <div style={{color: 'lightGreen'}}>{score.rank_change + "⏶"}</div>
        } else if(score.rank_change < 0) {
            return <div style={{color: '#ff5757'}}>{score.rank_change + "⏷"}</div>
        }
    }

    if(showAllResults) return <HighScores scoresEndpoint={'/leaderboard/get-leaderboard?type=' + type} title={'Totals'} isControl={true} />;

    if (!currentText) return;
    if (!scores) return;

    return <div style={{textAlign: 'center', fontSize: 80, width: '1500px', height: '900px', alignSelf: 'flex-start', display: 'flex', alignItems: 'flex-end', position: 'relative'}}>
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: '#000',
            zIndex: -1,
        }}/>
        <div style={{display: 'flex', flexWrap: 'wrap', width: '100%', height: '100%', justifyContent: 'space-evenly', alignItems: 'center', flexDirection: 'column'}}>
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    width: '1500px',
                    zIndex: 10,
                    fontSize: podium ? 50 : 80,
                }}
            ><SpokenLine speech={line} clockDrift={clockDrift}/></div>
            {podium && scores[0] ? (
                <PodiumCard key={current.role} entry={scores[0]} spot={podium.spot} label={podium.label} revealed={current.reveal}/>
            ) : (<>
            <div style={{flex: 1}}>
                <div></div>
            </div>
            <div style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: scores.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                gap: 12,
                width: '100%',
                boxSizing: 'border-box',
            }}>
                {scores.map((score, idx) => {
                    const isOrphan = scores.length > 1 && scores.length % 2 === 1 && idx === scores.length - 1;
                    return <div key={score.character.token} className={`highscores-${score.rank <= 5 ? score.rank : 5}`}
                         style={{
                             display: 'flex',
                             alignItems: 'center',
                             justifyContent: 'center',
                             height: '146px',
                             overflow: 'hidden',
                             position: 'relative',
                             boxSizing: 'border-box',
                             ...(isOrphan ? {
                                 gridColumn: '1 / -1',
                                 justifySelf: 'center',
                                 width: 'calc(50% - 6px)',
                             } : {}),
                         }}>
                        <div style={{textAlign: 'center', paddingRight: 6, paddingLeft: 6, width: 60, zIndex: 1, flexShrink: 0}}>
                            <div style={{
                                fontSize: 64,
                                fontWeight: 900,
                                fontStyle: 'italic',
                                color: rankAccent(score.rank),
                                textShadow: `0 0 18px ${rankAccent(score.rank)}aa, 2px 2px 0 rgba(0,0,0,0.95)`,
                            }}>{score.rank}</div>
                        </div>
                        <div style={{alignSelf: 'flex-start', fontSize: 50, marginRight: 12, zIndex: 1}}>
                            {indicator(score)}
                        </div>
                        <img
                            src={score.character.image_url}
                            alt={score.character.name}
                            style={{
                                width: 160,
                                height: 160,
                                objectFit: 'cover',
                                objectPosition: 'top',
                                alignSelf: 'flex-start',
                                flexShrink: 0,
                                filter: STROKE_FILTER,
                                willChange: 'filter',
                            }}
                        />
                        <div style={{paddingLeft: 10, flex: 1, textAlign: 'left', minWidth: 0}}>
                            <div style={{
                                fontSize: 32,
                                fontWeight: 800,
                                lineHeight: 1.05,
                                textShadow: '2px 2px 0 rgba(0,0,0,0.9)',
                                overflowWrap: 'break-word',
                            }}>{score.character.name}</div>
                            <div style={{
                                fontSize: 24,
                                fontStyle: 'italic',
                                lineHeight: 1.05,
                                opacity: 0.9,
                                textShadow: '1px 1px 0 rgba(0,0,0,0.9)',
                                overflowWrap: 'break-word',
                            }}>{score.character.nickname}</div>
                        </div>
                        <div style={{fontSize: 76, marginLeft: 8, marginRight: 12, textAlign: 'right', flexShrink: 0}}>
                            {score.score}
                        </div>
                    </div>;
                })}
            </div>
            </>)}
        </div>
    </div>
}

const PodiumCard = ({entry, spot, label, revealed}) => {
    const accent = rankAccent(spot);
    return <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 60,
        paddingTop: 160,
    }}>
        <img
            src={entry.character.image_url}
            alt=""
            style={{
                width: 380,
                height: 380,
                objectFit: 'cover',
                objectPosition: 'top',
                filter: revealed
                    ? `${STROKE_FILTER} drop-shadow(0 0 28px ${accent}66)`
                    : `brightness(0) drop-shadow(0 0 28px ${accent}88)`,
                transition: 'filter 500ms ease',
                flexShrink: 0,
            }}
        />
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 14, textAlign: 'left', maxWidth: 620}}>
            <div style={{
                fontSize: 44,
                fontWeight: 900,
                fontStyle: 'italic',
                letterSpacing: 10,
                color: accent,
                textShadow: `0 0 24px ${accent}aa, 3px 3px 0 rgba(0,0,0,0.95)`,
            }}>{label}</div>
            <div style={{fontSize: 64, fontWeight: 800, lineHeight: 1.05, textShadow: '2px 2px 0 rgba(0,0,0,0.9)'}}>
                {revealed ? entry.character.name : '???'}
            </div>
            <div style={{fontSize: 32, fontStyle: 'italic', opacity: revealed ? 0.9 : 0, transition: 'opacity 400ms', textShadow: '1px 1px 0 rgba(0,0,0,0.9)', minHeight: 36}}>
                {entry.character.nickname}
            </div>
            <AnimatedPoints from={0} to={entry.score} go={revealed}/>
        </div>
    </div>;
}